// src/pages/Enterprises.js
import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Pagination,
    TextField,
    Chip,
    Avatar,
    Button,
    Grid,
    CircularProgress,
    Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import axios from 'axios';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';

// Org list now comes through the authed T6 admin proxy (Firebase bearer), reusing
// the exported base + auth from the Agent Access tab.
import { T6_BASE, authHeader } from './enterprise/EnterpriseAgentAccessTab';
// ORG-PROVISION-FROM-PANEL-01 Part B: "Add enterprise" replaces the old "New Enterprise"
// dialog, which posted to org-backend's /api/orgs/register (now answered as gone).
import AddEnterpriseDialog, { refusalLine, sendOwnerInvitation } from './enterprise/AddEnterpriseDialog';
import { THEME } from './enterprise-ai/genesisData';
// PANEL-ORG-AND-LOADS-UI-01 C: "Start a bank transfer" on each row, the same dialog the
// Money page opens, with the row's organisation already picked.
import { StartBankTransferDialog, useBankTransferAvailability } from './enterprise-ai/MoneyPage';

// ─────────────────────────────────────────────
// STYLED COMPONENTS
// ─────────────────────────────────────────────

const PageContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    padding: '1.5rem',
    backgroundColor: '#1c1c1c',
    minHeight: '100vh',
});

const HeaderContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '1rem',
});

const Title = styled(Typography)({
    fontSize: '1.8rem',
    color: '#fff',
    fontWeight: 'bold',
});

const SearchBar = styled(TextField)({
    width: '300px',
    backgroundColor: '#202020',
    borderRadius: '8px',
    '& .MuiInputBase-root': {
        color: '#fff',
        fontSize: '0.95rem',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#12c099',
    },
    '& .MuiInputLabel-root': {
        color: '#12c099',
    },
});

const StyledTableContainer = styled(TableContainer)({
    backgroundColor: '#202020',
    borderRadius: '12px',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
});

const StyledTableCell = styled(TableCell)({
    padding: '14px 16px',
    fontSize: '0.9rem',
    color: '#fff',
    borderBottom: '1px solid #333',
});

const StyledTableRow = styled(TableRow)({
    '&:hover': {
        backgroundColor: 'rgba(18, 192, 153, 0.08)',
    },
    cursor: 'pointer',
});

const StatsCard = styled(Box)({
    backgroundColor: '#202020',
    borderRadius: '12px',
    padding: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
});

const AddButton = styled(Button)({
    backgroundColor: '#12c099',
    color: '#000',
    fontWeight: 700,
    textTransform: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    '&:hover': {
        backgroundColor: '#0fa882',
    },
});

// ─────────────────────────────────────────────
// OWNER COLUMN (ORG-PROVISION-FROM-PANEL-01 Part B)
// `owner` comes from the panel backend's GET /api/admin/orgs (services/
// orgOwnerStatusService.js). ownerUnread means that read failed; the backend logged
// [PANEL_ORG_OWNER_STATUS_UNREAD] and the cell says so rather than guessing.
// ─────────────────────────────────────────────

function OwnerInviteCell({ org, onSent }) {
    const [sending, setSending] = useState(false);
    const [err, setErr] = useState(null);
    const owner = org.owner;

    const send = async (e) => {
        // The row is a Link; the button must not navigate.
        e.preventDefault();
        e.stopPropagation();
        setSending(true);
        setErr(null);
        try {
            const data = await sendOwnerInvitation(org._id);
            if (data && data.inviteSent) onSent();
            else setErr('OWNER_INVITE_FAILED: ' + ((data && data.inviteError) || 'the invitation was not sent.'));
        } catch (e2) {
            setErr(refusalLine(e2));
        } finally {
            setSending(false);
        }
    };

    const line = (text, color) => (
        <Typography sx={{ color, fontSize: '0.8rem' }}>{text}</Typography>
    );

    if (org.ownerUnread) return line('Owner status could not be read', THEME.textMuted);
    if (!owner) return line('No Owner on record', THEME.textMuted);
    if (owner.signedIn) return line('Signed in', THEME.mint);

    let status;
    if (owner.invited === null) status = line('Not signed in yet', THEME.amber);
    else if (owner.invited) {
        status = line('Invited ' + new Date(owner.invitationSentAt).toLocaleDateString() + ', not signed in yet', THEME.amber);
    } else status = line('Invitation not sent yet', THEME.amber);

    return (
        <Box>
            {status}
            {owner.invited !== null && (
                <Button
                    size="small"
                    onClick={send}
                    disabled={sending}
                    sx={{ mt: 0.5, px: 1, minWidth: 0, color: THEME.mint, border: `1px solid ${THEME.mint}`, textTransform: 'none', fontSize: '0.75rem' }}
                >
                    {sending ? 'Sending...' : (owner.invited ? 'Send again' : 'Send invitation')}
                </Button>
            )}
            {err && <Typography sx={{ color: THEME.pink, fontSize: '0.72rem', mt: 0.5, maxWidth: 220 }}>{err}</Typography>}
        </Box>
    );
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────

export default function Enterprises() {
    const [enterprises, setEnterprises] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('createdAt');
    const rowsPerPage = 15;

    const [addOpen, setAddOpen] = useState(false);
    const [transferOrg, setTransferOrg] = useState(null);
    const transferAvailability = useBankTransferAvailability();
    const transferUnavailable = transferAvailability.loading || transferAvailability.available === false;

    const openTransfer = (e, org) => {
        // The row is a Link; the button must not navigate.
        e.preventDefault();
        e.stopPropagation();
        if (!transferUnavailable) setTransferOrg(org);
    };

    // ─────────────────────────────────────────────
    // FETCH ENTERPRISES
    // ─────────────────────────────────────────────

    useEffect(() => {
        const fetchEnterprises = async () => {
            setLoading(true);
            setError(null);
            try {
                const headers = await authHeader();
                const response = await axios.get(`${T6_BASE}/api/admin/orgs`, { headers });
                const rows = Array.isArray(response.data) ? response.data : (response.data?.orgs ?? []);
                const apiEnterprises = rows.map(org => ({
                    ...org,
                    status: 'active',
                    employeeCount: org.employeeCount || 0,
                    activeSessions: org.activeSessions || 0,
                }));
                setEnterprises(apiEnterprises);
            } catch (err) {
                // Rule 3: no silent fake data. Surface the failure and render an
                // error/retry state instead of the removed DUMMY_ENTERPRISES.
                console.error('[Enterprises] Failed to fetch:', err);
                setEnterprises([]);
                setError('Could not load enterprises.');
            } finally {
                setLoading(false);
            }
        };
        fetchEnterprises();
    }, [reloadKey]);

    // ─────────────────────────────────────────────
    // SORTING & FILTERING
    // ─────────────────────────────────────────────

    const handleSortRequest = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const filteredEnterprises = enterprises.filter(e =>
        e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.industry?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sortedEnterprises = [...filteredEnterprises].sort((a, b) => {
        let compare = 0;
        if (orderBy === 'createdAt') {
            compare = new Date(a.createdAt) - new Date(b.createdAt);
        } else if (orderBy === 'employeeCount') {
            compare = (a.employeeCount || 0) - (b.employeeCount || 0);
        } else {
            compare = (a[orderBy] || '').toString().localeCompare((b[orderBy] || '').toString());
        }
        return order === 'asc' ? compare : -compare;
    });

    const paginatedEnterprises = sortedEnterprises.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    // Stats
    const stats = {
        total: enterprises.length,
        active: enterprises.filter(e => e.status === 'active').length,
        pending: enterprises.filter(e => e.status === 'pending').length,
        totalEmployees: enterprises.reduce((sum, e) => sum + (e.employeeCount || 0), 0),
    };

    return (
        <PageContainer>
            {/* HEADER */}
            <HeaderContainer>
                <Box>
                    <Title>Enterprise Accounts</Title>
                    <Typography sx={{ color: '#888', fontSize: '0.9rem' }}>
                        Manage corporate wellness programs
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <SearchBar
                        variant="outlined"
                        size="small"
                        placeholder="Search enterprises..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <AddButton
                        startIcon={<AddIcon />}
                        onClick={() => setAddOpen(true)}
                    >
                        Add enterprise
                    </AddButton>
                </Box>
            </HeaderContainer>

            {/* STATS ROW */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <StatsCard>
                        <Typography sx={{ color: '#888', fontSize: '0.8rem' }}>Total Enterprises</Typography>
                        <Typography sx={{ color: '#fff', fontSize: '1.5rem', fontWeight: 700 }}>{stats.total}</Typography>
                    </StatsCard>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <StatsCard>
                        <Typography sx={{ color: '#888', fontSize: '0.8rem' }}>Active</Typography>
                        <Typography sx={{ color: '#12c099', fontSize: '1.5rem', fontWeight: 700 }}>{stats.active}</Typography>
                    </StatsCard>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <StatsCard>
                        <Typography sx={{ color: '#888', fontSize: '0.8rem' }}>Pending Setup</Typography>
                        <Typography sx={{ color: '#f59e0b', fontSize: '1.5rem', fontWeight: 700 }}>{stats.pending}</Typography>
                    </StatsCard>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <StatsCard>
                        <Typography sx={{ color: '#888', fontSize: '0.8rem' }}>Total Employees</Typography>
                        <Typography sx={{ color: '#fff', fontSize: '1.5rem', fontWeight: 700 }}>{stats.totalEmployees.toLocaleString()}</Typography>
                    </StatsCard>
                </Grid>
            </Grid>

            {/* TABLE */}
            <StyledTableContainer component={Paper}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress sx={{ color: '#12c099' }} />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6, px: 2 }}>
                        <Alert severity="error" sx={{ bgcolor: '#2a2a2a', color: '#e57373', border: '1px solid #442222' }}>
                            {error}
                        </Alert>
                        <Button
                            variant="outlined"
                            onClick={() => setReloadKey(k => k + 1)}
                            sx={{ color: '#12c099', borderColor: '#12c099', textTransform: 'none' }}
                        >
                            Retry
                        </Button>
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>
                                    <TableSortLabel
                                        active={orderBy === 'name'}
                                        direction={orderBy === 'name' ? order : 'asc'}
                                        onClick={() => handleSortRequest('name')}
                                        sx={{ color: '#12c099 !important' }}
                                    >
                                        Company
                                    </TableSortLabel>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <TableSortLabel
                                        active={orderBy === 'industry'}
                                        direction={orderBy === 'industry' ? order : 'asc'}
                                        onClick={() => handleSortRequest('industry')}
                                        sx={{ color: '#12c099 !important' }}
                                    >
                                        Industry
                                    </TableSortLabel>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <TableSortLabel
                                        active={orderBy === 'employeeCount'}
                                        direction={orderBy === 'employeeCount' ? order : 'asc'}
                                        onClick={() => handleSortRequest('employeeCount')}
                                        sx={{ color: '#12c099 !important' }}
                                    >
                                        Employees
                                    </TableSortLabel>
                                </StyledTableCell>
                                <StyledTableCell>Location</StyledTableCell>
                                <StyledTableCell>Contact</StyledTableCell>
                                <StyledTableCell>Owner</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell>
                                    <TableSortLabel
                                        active={orderBy === 'createdAt'}
                                        direction={orderBy === 'createdAt' ? order : 'asc'}
                                        onClick={() => handleSortRequest('createdAt')}
                                        sx={{ color: '#12c099 !important' }}
                                    >
                                        Added
                                    </TableSortLabel>
                                </StyledTableCell>
                                <StyledTableCell>Money</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedEnterprises.map((enterprise) => (
                                <StyledTableRow
                                    key={enterprise._id}
                                    component={Link}
                                    to={`/enterprise/${enterprise.slug || enterprise._id}`}
                                    sx={{ textDecoration: 'none' }}
                                >
                                    <StyledTableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar sx={{ bgcolor: '#12c099', width: 36, height: 36 }}>
                                                <BusinessIcon sx={{ fontSize: 20 }} />
                                            </Avatar>
                                            <Box>
                                                <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>
                                                    {enterprise.name}
                                                </Typography>
                                                <Typography sx={{ color: '#666', fontSize: '0.75rem' }}>
                                                    {enterprise.emailDomains?.[0] || '—'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip
                                            label={enterprise.industry || 'N/A'}
                                            size="small"
                                            sx={{
                                                bgcolor: '#333',
                                                color: '#fff',
                                                fontSize: '0.75rem',
                                            }}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <GroupIcon sx={{ fontSize: 16, color: '#888' }} />
                                            <Typography sx={{ color: '#fff' }}>
                                                {enterprise.employeeCount?.toLocaleString() || '—'}
                                            </Typography>
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <LocationOnIcon sx={{ fontSize: 16, color: '#888' }} />
                                            <Typography sx={{ color: '#ccc', fontSize: '0.85rem' }}>
                                                {enterprise.address?.city && enterprise.address?.state
                                                    ? `${enterprise.address.city}, ${enterprise.address.state}`
                                                    : '—'}
                                            </Typography>
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Box>
                                            <Typography sx={{ color: '#fff', fontSize: '0.85rem' }}>
                                                {enterprise.contactName || '—'}
                                            </Typography>
                                            <Typography sx={{ color: '#666', fontSize: '0.75rem' }}>
                                                {enterprise.contactEmail || ''}
                                            </Typography>
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <OwnerInviteCell org={enterprise} onSent={() => setReloadKey(k => k + 1)} />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip
                                            icon={enterprise.status === 'active'
                                                ? <CheckCircleIcon sx={{ fontSize: 14 }} />
                                                : <PendingIcon sx={{ fontSize: 14 }} />
                                            }
                                            label={enterprise.status === 'active' ? 'Active' : 'Pending'}
                                            size="small"
                                            sx={{
                                                bgcolor: enterprise.status === 'active' ? 'rgba(18,192,153,0.15)' : 'rgba(245,158,11,0.15)',
                                                color: enterprise.status === 'active' ? '#12c099' : '#f59e0b',
                                                fontSize: '0.75rem',
                                                '& .MuiChip-icon': {
                                                    color: enterprise.status === 'active' ? '#12c099' : '#f59e0b',
                                                }
                                            }}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>
                                            {enterprise.createdAt
                                                ? new Date(enterprise.createdAt).toLocaleDateString()
                                                : '—'}
                                        </Typography>
                                    </StyledTableCell>
                                    {/* A disabled button lets the click through to the row Link; the cell stops it. */}
                                    <StyledTableCell onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                                        <Button
                                            size="small"
                                            onClick={(e) => openTransfer(e, enterprise)}
                                            disabled={transferUnavailable}
                                            title={transferAvailability.available === false ? transferAvailability.message : undefined}
                                            sx={{ px: 1, minWidth: 0, color: THEME.mint, border: `1px solid ${THEME.mint}`, textTransform: 'none', fontSize: '0.75rem', whiteSpace: 'nowrap', '&.Mui-disabled': { color: THEME.textMuted, borderColor: THEME.border } }}
                                        >
                                            Start a bank transfer
                                        </Button>
                                        {transferAvailability.available === false && (
                                            <Typography sx={{ color: THEME.textMuted, fontSize: '0.7rem', mt: 0.5, maxWidth: 200 }}>Not available yet: org-backend needs one route</Typography>
                                        )}
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </StyledTableContainer>

            {/* PAGINATION */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                    count={Math.ceil(filteredEnterprises.length / rowsPerPage)}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    sx={{
                        '& .MuiPaginationItem-root': { color: '#888' },
                        '& .Mui-selected': { bgcolor: '#12c099 !important', color: '#000' },
                    }}
                />
            </Box>

            <AddEnterpriseDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onCreated={() => setReloadKey(k => k + 1)}
            />
            <StartBankTransferDialog
                open={!!transferOrg}
                orgs={enterprises}
                initialOrg={transferOrg}
                availability={transferAvailability}
                onClose={() => setTransferOrg(null)}
            />
        </PageContainer>
    );
}
